import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getToken } from "../../util/authUtils";
import { getApiBaseUrl } from "../../util/apiBaseUrl";

function getConnectToken(search) {
  return new URLSearchParams(search).get("token") || "";
}

async function readJsonResponse(response, fallbackMessage) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (_error) {
    return {
      ok: false,
      error: response.ok ? fallbackMessage : text || fallbackMessage,
    };
  }
}

export default function ConnectTelegramPage() {
  const location = useLocation();
  const connectToken = useMemo(() => getConnectToken(location.search), [location.search]);
  const returnTo = `/connect/telegram?token=${encodeURIComponent(connectToken)}`;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const authToken = getToken();
  const [loading, setLoading] = useState(true);
  const [linkInfo, setLinkInfo] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const providerName = linkInfo?.provider === "whatsapp" ? "WhatsApp" : "Telegram";

  useEffect(() => {
    let alive = true;
    async function loadLink() {
      if (!connectToken) {
        setError("This connection link is missing a token.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${apiBaseUrl}/api/capture/telegram/link/${encodeURIComponent(connectToken)}`);
        const payload = await readJsonResponse(response, "Connection service is unavailable.");
        if (!response.ok || !payload.ok) {
          throw new Error(payload.error || "Connection link is invalid or expired.");
        }
        if (!alive) return;
        setLinkInfo(payload.link);
        setDraft(payload.draft || null);
      } catch (err) {
        if (alive) setError(err.message || "Could not load connection.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    loadLink();
    return () => {
      alive = false;
    };
  }, [connectToken]);

  const handleConnect = async () => {
    setConnecting(true);
    setError("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/capture/telegram/link`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: authToken ? `Bearer ${authToken}` : "",
        },
        body: JSON.stringify({ token: connectToken }),
      });
      const payload = await readJsonResponse(response, "Connection service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not connect account.");
      }
      setDraft(payload.draft || draft);
      setConnected(true);
    } catch (err) {
      setError(err.message || "Could not connect account.");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "background.default", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Stack
          spacing={3}
          sx={{
            bgcolor: "background.paper",
            border: 1, borderColor: "divider",
            borderRadius: 2,
            p: { xs: 3, md: 4 },
            boxShadow: "0 12px 30px rgba(34, 55, 74, 0.08)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TelegramIcon sx={{ color: "#229ED9", fontSize: 34 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                Connect {providerName}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                Save event posts, posters, screenshots, and address images into Timely365 Capture.
              </Typography>
            </Box>
          </Stack>

          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography>Checking your connection link...</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : connected ? (
            <Stack spacing={2}>
              <Alert icon={<CheckCircleOutlineIcon />} severity="success">
                {providerName} is connected. Return to {providerName}, or review captures in Timely365.
              </Alert>
              <Button
                component={RouterLink}
                to="/biz/capture"
                variant="contained"
                sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              >
                Open Capture Inbox
              </Button>
            </Stack>
          ) : (
            <>
              <Alert severity="info">
                Connecting {linkInfo?.displayName || linkInfo?.username || `this ${providerName} account`} to
                Timely365.
              </Alert>

              {draft?.extracted && (
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                    Pending draft
                  </Typography>
                  <Typography>{draft.extracted.title || "Untitled event"}</Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {[draft.extracted.date, draft.extracted.startTime].filter(Boolean).join(" at ")}
                  </Typography>
                  {draft.extracted.location && (
                    <Typography sx={{ color: "text.secondary" }}>{draft.extracted.location}</Typography>
                  )}
                </Box>
              )}

              {authToken ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={connecting ? <CircularProgress color="inherit" size={18} /> : <TelegramIcon />}
                  onClick={handleConnect}
                  disabled={connecting}
                  sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                >
                  Connect {providerName}
                </Button>
              ) : (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    component={RouterLink}
                    to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
                    variant="contained"
                    startIcon={<LoginIcon />}
                    sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                  >
                    Sign in
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`/start-free-trial?returnTo=${encodeURIComponent(returnTo)}`}
                    variant="outlined"
                    startIcon={<PersonAddAlt1Icon />}
                    sx={{ borderColor: "#22374a", color: "text.primary" }}
                  >
                    Create account
                  </Button>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
